/**
 * EnjoyOverlaysPanel — /admin tab for managing lc_events_overlays.
 *
 * - List · search · filter (status / country / kind / human_confirmed)
 * - Confirm / archive / delete
 * - Add new overlay
 * - Export CSV
 *
 * Uses the same `api` (Bearer JWT) client as the other /admin panels.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const STATUS_OPTIONS = ["", "active", "archived", "expired", "awaiting_next_date"];
const KIND_OPTIONS = ["", "event", "experience"];
// Founder ruling 14 Feb 2026 PM-5: every category Concierge® must serve
// is editable from this panel. Mirrors backend/enjoy/enjoy_platform_registry.json
// category_catalog · Awards, Spirituality, Adventure included.
const CATEGORY_OPTIONS = [
  "music", "theatre", "sports", "festivals", "comedy", "family", "arts",
  "club_nights", "brunches", "food_and_drinks", "exhibitions",
  "workshops", "adventure", "awards_shows", "spirituality",
  "conferences", "film",
];

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

const ROUTE_KINDS = [
  "official", "ticketmaster", "bookmyshow", "district",
  "dice", "axs", "seetickets", "seatgeek",
  "ticketek", "platinumlist", "eventim", "ticketone", "fnac",
  "venue", "read_more", "brochure",
];

function BookingRoutesEditor({ value, onChange }) {
  const routes = Array.isArray(value) ? value : [];
  function update(i, patch) {
    const next = [...routes];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function remove(i) { onChange(routes.filter((_, j) => j !== i)); }
  function move(i, delta) {
    const j = i + delta;
    if (j < 0 || j >= routes.length) return;
    const next = [...routes];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  function add() {
    onChange([...routes, { kind: "official", url: "", label: "", verified: false, audience: "member" }]);
  }
  return (
    <div className="space-y-2" data-testid="enjoy-overlay-routes">
      {routes.length === 0 && (
        <p className="text-[13px] text-[hsl(var(--muted))] italic"
           data-testid="enjoy-overlay-routes-empty">
          No routes yet · the resolver will render this as Concierge® only.
        </p>
      )}
      {routes.map((r, i) => (
        <div key={i} className="grid md:grid-cols-12 gap-2 items-start p-3 rounded border border-[hsl(var(--brass)/0.3)] bg-[hsl(var(--paper)/0.4)]"
             data-testid={`enjoy-overlay-route-row-${i}`}>
          <div className="md:col-span-2">
            <select value={r.kind || "official"}
              onChange={(e) => update(i, { kind: e.target.value })}
              className="w-full p-2 text-[13px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
              data-testid={`enjoy-overlay-route-${i}-kind`}>
              {ROUTE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <input value={r.label || ""}
              placeholder="Label"
              onChange={(e) => update(i, { label: e.target.value })}
              className="w-full p-2 text-[13px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
              data-testid={`enjoy-overlay-route-${i}-label`} />
          </div>
          <div className="md:col-span-5">
            <input value={r.url || ""}
              placeholder="https://…"
              onChange={(e) => update(i, { url: e.target.value })}
              className="w-full p-2 text-[13px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
              data-testid={`enjoy-overlay-route-${i}-url`} />
          </div>
          <div className="md:col-span-2 flex items-center gap-3 text-[12px] text-[hsl(var(--muted))]">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={!!r.verified}
                onChange={(e) => update(i, { verified: e.target.checked })}
                data-testid={`enjoy-overlay-route-${i}-verified`} />
              verified
            </label>
            <select value={r.audience || "member"}
              onChange={(e) => update(i, { audience: e.target.value })}
              className="p-1 text-[11px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
              data-testid={`enjoy-overlay-route-${i}-audience`}>
              <option value="member">member</option>
              <option value="desk">desk-only</option>
            </select>
          </div>
          <div className="md:col-span-1 flex gap-1 justify-end">
            <button type="button" className="text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--muted))] hover:text-[hsl(var(--ink))] px-1"
              onClick={() => move(i, -1)} disabled={i === 0}
              data-testid={`enjoy-overlay-route-${i}-up`}>↑</button>
            <button type="button" className="text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--muted))] hover:text-[hsl(var(--ink))] px-1"
              onClick={() => move(i, 1)} disabled={i === routes.length - 1}
              data-testid={`enjoy-overlay-route-${i}-down`}>↓</button>
            <button type="button" className="text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--destructive))] hover:underline px-1"
              onClick={() => remove(i)}
              data-testid={`enjoy-overlay-route-${i}-remove`}>×</button>
          </div>
        </div>
      ))}
      <button type="button"
        className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--brass))] hover:text-[hsl(var(--brass-deep))] mt-2"
        onClick={add}
        data-testid="enjoy-overlay-routes-add">+ Add route</button>
    </div>
  );
}

function EditDrawer({ overlay, onSaved, onClose }) {
  const isNew = !overlay?.id;
  const [draft, setDraft] = useState(() => ({
    title: "",
    category: "music",
    kind: "event",
    city: "",
    country: "",
    venue_name: "",
    start_at: "",
    end_at: "",
    tz: "",
    price_from: "",
    currency: "",
    image_url: "",
    direct_book_url: "",
    concierge_whisper: "",
    season_category: "",
    date_note: "",
    collection: "",
    priority: 50,
    status: "active",
    fulfilment_route: "",
    booking_routes: [],
    force_concierge: false,
    ...overlay,
  }));
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function set(k) { return (e) => setDraft((d) => ({ ...d, [k]: e.target.value })); }

  async function save() {
    setBusy(true); setMsg(""); setErr("");
    try {
      // Clean payload — strip empty strings into null for date fields
      const payload = { ...draft };
      ["start_at", "end_at"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
        else if (typeof payload[k] === "string" && payload[k].length === 10) {
          payload[k] = `${payload[k]}T00:00:00Z`;
        }
      });
      if (payload.price_from === "") payload.price_from = null;
      else payload.price_from = Number(payload.price_from);
      payload.priority = Number(payload.priority) || 50;

      if (isNew) {
        const { data } = await api.post("/admin/enjoy/overlays", payload);
        onSaved(data);
      } else {
        const { data } = await api.patch(`/admin/enjoy/overlays/${overlay.id}`, payload);
        onSaved(data);
      }
      setMsg("Saved.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function confirm() {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/enjoy/overlays/${overlay.id}/confirm`);
      onSaved(data);
      setMsg("Confirmed.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  async function archive() {
    if (!window.confirm("Archive this overlay? It will stop appearing on /enjoy.")) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/enjoy/overlays/${overlay.id}/archive`);
      onSaved(data);
      setMsg("Archived.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  async function del() {
    if (!window.confirm("Delete this overlay permanently? This cannot be undone.")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/enjoy/overlays/${overlay.id}`);
      onSaved(null); // null = deleted
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[hsl(var(--navy)/0.85)] overflow-y-auto"
         onClick={onClose} data-testid="enjoy-overlay-drawer">
      <div className="min-h-full flex items-start justify-center p-4 md:p-10">
        <div className="bg-[hsl(var(--ivory))] w-full max-w-3xl border border-[hsl(var(--brass))]"
             onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4 p-6 border-b border-[hsl(var(--brass)/0.4)]">
            <div>
              <p className="eyebrow">{isNew ? "New overlay" : "Edit overlay"}</p>
              <h2 className="font-serif text-2xl mt-1">{draft.title || "—"}</h2>
              <p className="text-[12px] text-[hsl(var(--muted))] mt-1">
                {overlay?.human_confirmed ? "✓ Confirmed" : "○ Unconfirmed"}
                {overlay?.confirmed_by && ` · by ${overlay.confirmed_by}`}
                {overlay?.confirmed_at && ` · ${fmt(overlay.confirmed_at)}`}
              </p>
            </div>
            <button onClick={onClose} className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--muted))] hover:text-[hsl(var(--brass))]"
              data-testid="enjoy-overlay-close">Close ✕</button>
          </div>

          <div className="p-6 grid md:grid-cols-2 gap-5 text-[14px]">
            <Field label="Title" required testid="title"><input value={draft.title} onChange={set("title")} /></Field>
            <Field label="Category" testid="category">
              <select value={draft.category} onChange={set("category")}>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Kind" testid="kind">
              <select value={draft.kind} onChange={set("kind")}>
                <option value="event">event</option>
                <option value="experience">experience</option>
              </select>
            </Field>
            <Field label="Status" testid="status">
              <select value={draft.status} onChange={set("status")}>
                <option value="active">active</option>
                <option value="archived">archived</option>
                <option value="expired">expired</option>
              </select>
            </Field>
            <Field label="Venue" testid="venue"><input value={draft.venue_name || ""} onChange={set("venue_name")} /></Field>
            <Field label="City" testid="city"><input value={draft.city || ""} onChange={set("city")} /></Field>
            <Field label="Country (ISO-2)" testid="country"><input value={draft.country || ""} onChange={set("country")} maxLength={2} /></Field>
            <Field label="Timezone" testid="tz"><input value={draft.tz || ""} onChange={set("tz")} placeholder="Europe/London" /></Field>
            <Field label="Start (YYYY-MM-DD)" testid="start_at"><input type="date" value={(draft.start_at || "").slice(0,10)} onChange={set("start_at")} /></Field>
            <Field label="End (YYYY-MM-DD)" testid="end_at"><input type="date" value={(draft.end_at || "").slice(0,10)} onChange={set("end_at")} /></Field>
            <Field label="Price from" testid="price_from"><input type="number" value={draft.price_from ?? ""} onChange={set("price_from")} /></Field>
            <Field label="Currency" testid="currency"><input value={draft.currency || ""} onChange={set("currency")} placeholder="GBP" maxLength={3} /></Field>
            <Field label="Image URL" testid="image_url" full><input value={draft.image_url || ""} onChange={set("image_url")} /></Field>
            <Field label="Direct book URL" testid="direct_book_url" full><input value={draft.direct_book_url || ""} onChange={set("direct_book_url")} /></Field>
            <Field label="Concierge whisper" testid="whisper" full>
              <textarea rows={3} value={draft.concierge_whisper || ""} onChange={set("concierge_whisper")} />
            </Field>
            <Field label="Season category" testid="season"><input value={draft.season_category || ""} onChange={set("season_category")} /></Field>
            <Field label="Collection" testid="collection"><input value={draft.collection || ""} onChange={set("collection")} /></Field>
            <Field label="Date note (for undated experiences)" testid="date_note" full><input value={draft.date_note || ""} onChange={set("date_note")} /></Field>
            {draft.kind === "experience" && (
              <Field label="Fulfilment route (required to confirm)" testid="fulfilment_route" full>
                <input value={draft.fulfilment_route || ""} onChange={set("fulfilment_route")} placeholder="Network partner, supplier name, etc." />
              </Field>
            )}
            <Field label="Priority (higher = surfaces first)" testid="priority"><input type="number" value={draft.priority ?? 50} onChange={set("priority")} /></Field>
          </div>

          {/* ── Booking routes editor · Addendum B v2 ── */}
          <div className="px-6 pb-6 pt-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] mb-1.5">
                  Booking ladder (desk order · first verified entry surfaces as card chip)
                </label>
                <p className="text-[12px] text-[hsl(var(--muted))]">
                  Resale (Vivid Seats / StubHub / viagogo) is never rendered to members.
                  Add a route, paste the URL, mark verified once the desk has confirmed.
                </p>
              </div>
              <label className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] whitespace-nowrap">
                <input type="checkbox" checked={!!draft.force_concierge}
                  onChange={(e) => setDraft((d) => ({ ...d, force_concierge: e.target.checked }))}
                  data-testid="enjoy-overlay-force-concierge" />
                Force Concierge® only
              </label>
            </div>
            <BookingRoutesEditor
              value={draft.booking_routes || []}
              onChange={(v) => setDraft((d) => ({ ...d, booking_routes: v }))}
            />
          </div>

          {msg && <div className="px-6 pb-2 text-[13px] text-[hsl(var(--brass))]">{msg}</div>}
          {err && <div className="px-6 pb-2 text-[13px] text-[hsl(var(--destructive))]">{err}</div>}

          <div className="px-6 py-5 border-t border-[hsl(var(--brass)/0.4)] flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3 flex-wrap">
              <button className="btn-primary disabled:opacity-50" disabled={busy} onClick={save}
                data-testid="enjoy-overlay-save">{busy ? "Saving…" : "Save"}</button>
              {!isNew && !overlay?.human_confirmed && (
                <button className="btn-ghost" disabled={busy} onClick={confirm}
                  data-testid="enjoy-overlay-confirm">Confirm</button>
              )}
              {!isNew && (
                <button className="btn-ghost" disabled={busy} onClick={archive}
                  data-testid="enjoy-overlay-archive">Archive</button>
              )}
            </div>
            {!isNew && (
              <button className="text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--destructive))] hover:underline"
                onClick={del} disabled={busy} data-testid="enjoy-overlay-delete">Delete permanently</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, full, testid, required, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] mb-1.5">
        {label}{required && " *"}
      </label>
      {React.cloneElement(children, {
        "data-testid": `enjoy-overlay-field-${testid}`,
        className: "w-full p-2.5 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)] focus:outline-none focus:border-[hsl(var(--brass))]",
      })}
    </div>
  );
}

export default function EnjoyOverlaysPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [confirmedFilter, setConfirmedFilter] = useState(""); // "", "true", "false"
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = { page, page_size: 100 };
      if (status) params.status = status;
      if (kind) params.kind = kind;
      if (q) params.q = q;
      if (confirmedFilter !== "") params.human_confirmed = confirmedFilter;
      const { data } = await api.get("/admin/enjoy/overlays", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [page, status, kind, q, confirmedFilter]);

  // Status badge counts · refreshes when filters change so counts reflect
  // current scope (q/kind/confirmedFilter still in play).
  const loadStatusCounts = useCallback(async () => {
    try {
      const counts = {};
      // Three lightweight head counts · run in parallel
      const targets = ["awaiting_next_date", "expired", "active"];
      const results = await Promise.all(targets.map((s) =>
        api.get("/admin/enjoy/overlays", { params: { status: s, page_size: 1 } })
          .then((r) => [s, r.data.total || 0]).catch(() => [s, 0])
      ));
      for (const [s, c] of results) counts[s] = c;
      setStatusCounts(counts);
    } catch { /* counts are decorative · failure is non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatusCounts(); }, [loadStatusCounts]);

  function onSaved(updated) {
    if (updated === null) {
      // deleted
      setItems((prev) => prev.filter((x) => x.id !== editing?.id));
    } else {
      setItems((prev) => {
        const exists = prev.some((x) => x.id === updated.id);
        return exists
          ? prev.map((x) => (x.id === updated.id ? updated : x))
          : [updated, ...prev];
      });
    }
    setEditing(null);
  }

  async function exportCsv() {
    try {
      const { data } = await api.get("/admin/enjoy/overlays/export.csv", { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enjoy_overlays_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  // ── CSV import · founder ruling 14 Feb 2026 PM-5 ──────────────────────
  // Two-step: dry-run preview first (shows would-create/would-update
  // counts), then commit. Mirrors the export shape exactly so a desk
  // workflow is: Export → edit in Sheets → Import → preview → commit.
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [importFile, setImportFile] = useState(null);

  async function runImport(file, dryRun) {
    setImportBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/enjoy/overlays/import.csv?dry_run=${dryRun ? "true" : "false"}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportReport(data);
      if (!dryRun && data.ok) {
        // Reload list on successful commit · clear the stashed file
        setImportFile(null);
        await load();
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setImportBusy(false); }
  }

  function onPickImportFile(ev) {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = ""; // allow re-picking the same file
    if (!f) return;
    setImportFile(f);
    runImport(f, true); // always preview first
  }

  // ── Bulk confirm by collection · founder ruling 14 Feb 2026 PM (P2 pulled forward) ──
  const [collections, setCollections] = useState([]);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);

  async function openCollectionsModal() {
    setBulkSummary(null);
    try {
      const { data } = await api.get("/admin/enjoy/overlays/collections");
      setCollections(data.collections || []);
      setCollectionsOpen(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function runBulkConfirm(collection, unconfirmed) {
    const ok = window.confirm(
      `Confirm ${unconfirmed} unconfirmed event(s) in "${collection}"?\n\n` +
      `Per-event audit trail is preserved · each row gets confirmed_by=you ` +
      `+ confirmed_at + a confirmation_log entry, exactly like a single-row confirm.\n\n` +
      `(Experiences missing fulfilment_route will be skipped.)`
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/enjoy/overlays/bulk-confirm", { collection });
      setBulkSummary(data);
      await load();  // refresh list
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div data-testid="enjoy-overlays-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="eyebrow">lc_events_overlays</p>
          <h3 className="font-serif text-2xl mt-1">Enjoy overlays</h3>
          <p className="text-[13px] text-[hsl(var(--muted))] mt-1">
            {total} overlays · {items.filter((x) => x.human_confirmed).length} confirmed on this page
          </p>
          {/* ── Stale-event lifecycle queues · click to filter ── */}
          <div className="flex gap-3 mt-3 flex-wrap">
            {(statusCounts.awaiting_next_date ?? 0) > 0 && (
              <button
                onClick={() => { setStatus("awaiting_next_date"); setPage(1); }}
                className={`text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 rounded-full ${
                  status === "awaiting_next_date"
                    ? "bg-[hsl(var(--brass))] text-[hsl(var(--ivory))]"
                    : "border border-[hsl(var(--brass))] text-[hsl(var(--brass))] hover:bg-[hsl(var(--brass)/0.1)]"
                }`}
                data-testid="enjoy-overlays-queue-awaiting-next-date"
              >Awaiting next date · {statusCounts.awaiting_next_date}</button>
            )}
            {(statusCounts.expired ?? 0) > 0 && (
              <button
                onClick={() => { setStatus("expired"); setPage(1); }}
                className={`text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 rounded-full ${
                  status === "expired"
                    ? "bg-[hsl(var(--destructive))] text-[hsl(var(--ivory))]"
                    : "border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.1)]"
                }`}
                data-testid="enjoy-overlays-queue-expired"
              >Expired · {statusCounts.expired}</button>
            )}
            {status && (
              <button
                onClick={() => { setStatus(""); setPage(1); }}
                className="text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass)/0.4)] text-[hsl(var(--muted))] hover:text-[hsl(var(--ink))]"
                data-testid="enjoy-overlays-queue-clear"
              >× Clear filter</button>
            )}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button className="btn-ghost text-[11px] tracking-[0.2em] uppercase"
            onClick={openCollectionsModal}
            data-testid="enjoy-overlays-bulk-confirm-open">↑ Bulk confirm</button>
          <button className="btn-ghost text-[11px] tracking-[0.2em] uppercase"
            onClick={exportCsv}
            data-testid="enjoy-overlays-export-csv">↓ Export CSV</button>
          {/* Import CSV · two-step (dry-run preview, then commit) */}
          <label
            className="btn-ghost text-[11px] tracking-[0.2em] uppercase cursor-pointer"
            data-testid="enjoy-overlays-import-csv-label"
            style={{ opacity: importBusy ? 0.5 : 1 }}
          >
            ↑ Import CSV
            <input
              type="file" accept=".csv,text/csv"
              hidden disabled={importBusy}
              onChange={onPickImportFile}
              data-testid="enjoy-overlays-import-csv-input"
            />
          </label>
          <button className="btn-primary"
            onClick={() => setEditing({})} data-testid="enjoy-overlays-add">+ Add overlay</button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <input className="p-2.5 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)] focus:outline-none focus:border-[hsl(var(--brass))]"
          placeholder="Search title / venue / city…"
          value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          data-testid="enjoy-overlays-search" />
        <select className="p-2.5 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
          value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid="enjoy-overlays-status">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All status"}</option>)}
        </select>
        <select className="p-2.5 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
          value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}
          data-testid="enjoy-overlays-kind">
          {KIND_OPTIONS.map((s) => <option key={s} value={s}>{s || "All kinds"}</option>)}
        </select>
        <select className="p-2.5 text-[14px] bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)]"
          value={confirmedFilter} onChange={(e) => { setConfirmedFilter(e.target.value); setPage(1); }}
          data-testid="enjoy-overlays-confirmed">
          <option value="">All (confirmed + unconfirmed)</option>
          <option value="true">Confirmed only</option>
          <option value="false">Unconfirmed only</option>
        </select>
      </div>

      {err && <div className="mb-4 p-3 bg-[hsl(var(--destructive)/0.1)] border border-[hsl(var(--destructive)/0.4)] text-[13px] text-[hsl(var(--destructive))]" data-testid="enjoy-overlays-error">{err}</div>}

      {loading ? (
        <p className="eyebrow" data-testid="enjoy-overlays-loading">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[15px] text-[hsl(var(--muted))]" data-testid="enjoy-overlays-empty">No overlays match your filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]" data-testid="enjoy-overlays-table">
            <thead>
              <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] border-b border-[hsl(var(--brass)/0.4)]">
                <th className="py-3 pr-4">Title</th>
                <th className="py-3 pr-4">Cat.</th>
                <th className="py-3 pr-4">Kind</th>
                <th className="py-3 pr-4">City</th>
                <th className="py-3 pr-4">Country</th>
                <th className="py-3 pr-4">Start</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Confirmed?</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id} onClick={() => setEditing(x)}
                  className="border-b border-[hsl(var(--brass)/0.2)] cursor-pointer hover:bg-[hsl(var(--cream)/0.5)]"
                  data-testid={`enjoy-overlays-row-${x.id}`}>
                  <td className="py-3 pr-4 font-medium max-w-md truncate">{x.title}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))]">{x.category}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))]">{x.kind || "event"}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))] truncate max-w-[160px]">{x.city || "—"}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))]">{x.country || "—"}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))]">{fmt(x.start_at)}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted))]">{x.status}</td>
                  <td className="py-3 pr-4">{x.human_confirmed ? "✓" : "○"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <EditDrawer overlay={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}

      {collectionsOpen && (
        <div
          className="fixed inset-0 z-50 bg-[hsl(var(--navy)/0.7)] flex items-center justify-center p-6"
          onClick={() => !bulkBusy && setCollectionsOpen(false)}
          data-testid="enjoy-overlays-bulk-overlay"
        >
          <div
            className="bg-[hsl(var(--ivory))] w-full max-w-2xl border border-[hsl(var(--brass))]"
            onClick={(e) => e.stopPropagation()}
            data-testid="enjoy-overlays-bulk-modal"
          >
            <div className="p-6 border-b border-[hsl(var(--brass)/0.4)]">
              <p className="eyebrow">Bulk confirm by collection</p>
              <h3 className="font-serif text-2xl mt-1">Choose a collection to audit-confirm</h3>
              <p className="text-[13px] text-[hsl(var(--muted))] mt-2">
                Per-event attribution is preserved · each row receives
                <code className="px-1 mx-1">confirmed_by={"{you}"}</code> +
                <code className="px-1 mx-1">confirmation_log</code> entry +
                <code className="px-1 mx-1">bulk_confirm_batch_id</code>
                for forensic grouping. Experiences without a fulfilment route are skipped, not errored.
              </p>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {collections.length === 0 ? (
                <p className="text-[14px] text-[hsl(var(--muted))] p-4"
                   data-testid="enjoy-overlays-bulk-empty">
                  No collections found.
                </p>
              ) : (
                collections.map((c) => (
                  <div key={c.collection}
                       className="flex items-center justify-between gap-4 p-3 border-b border-[hsl(var(--brass)/0.2)]"
                       data-testid={`enjoy-overlays-bulk-row-${(c.collection || "").replace(/\W+/g, "-").toLowerCase()}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[15px] truncate">{c.collection}</p>
                      <p className="text-[12px] text-[hsl(var(--muted))] mt-1">
                        {c.total} total · <span className={c.unconfirmed > 0 ? "text-[hsl(var(--brass))]" : ""}>
                          {c.unconfirmed} unconfirmed
                        </span>
                      </p>
                    </div>
                    <button
                      className="btn-primary disabled:opacity-50"
                      disabled={bulkBusy || c.unconfirmed === 0}
                      onClick={() => runBulkConfirm(c.collection, c.unconfirmed)}
                      data-testid="enjoy-overlays-bulk-run"
                    >
                      {bulkBusy ? "Confirming…" : `Confirm ${c.unconfirmed}`}
                    </button>
                  </div>
                ))
              )}

              {bulkSummary && (
                <div className="m-3 p-4 bg-[hsl(var(--cream)/0.6)] border border-[hsl(var(--brass)/0.4)]"
                     data-testid="enjoy-overlays-bulk-summary">
                  <p className="font-serif text-[15px]">
                    Batch <code>{bulkSummary.batch_id}</code> complete.
                  </p>
                  <p className="text-[13px] text-[hsl(var(--muted))] mt-1">
                    <strong className="text-[hsl(var(--brass))]">{bulkSummary.confirmed} confirmed</strong>
                    {bulkSummary.skipped > 0 && (
                      <> · <span>{bulkSummary.skipped} skipped</span></>
                    )}
                  </p>
                  {bulkSummary.errors && bulkSummary.errors.length > 0 && (
                    <details className="mt-3 text-[12px]">
                      <summary className="cursor-pointer text-[hsl(var(--muted))]">
                        {bulkSummary.errors.length} row(s) skipped · why?
                      </summary>
                      <ul className="mt-2 space-y-1 pl-4 list-disc text-[hsl(var(--muted))]">
                        {bulkSummary.errors.slice(0, 20).map((e, i) => (
                          <li key={i}>{e.id} — {e.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[hsl(var(--brass)/0.4)] flex justify-end gap-3">
              <button className="btn-ghost text-[11px] tracking-[0.2em] uppercase"
                onClick={() => setCollectionsOpen(false)}
                disabled={bulkBusy}
                data-testid="enjoy-overlays-bulk-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import preview modal · founder ruling 14 Feb 2026 PM-5 */}
      {importReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
             style={{ background: "rgba(33,28,21,0.55)" }}>
          <div className="bg-[hsl(var(--paper))] border border-[hsl(var(--brass)/0.4)] w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col"
               data-testid="enjoy-overlays-import-modal">
            <div className="p-5 border-b border-[hsl(var(--brass)/0.4)]">
              <div className="t-mono uppercase text-[10px] tracking-[0.22em] text-[hsl(var(--brass))] mb-1.5">
                {importReport.dry_run ? "Import preview" : "Import complete"}
              </div>
              <h3 className="t-serif text-2xl">{importReport.filename}</h3>
              <div className="flex gap-4 mt-3 text-[12px]">
                <span data-testid="enjoy-import-stat-created">
                  <strong>{importReport.created}</strong> {importReport.dry_run ? "would create" : "created"}
                </span>
                <span data-testid="enjoy-import-stat-updated">
                  <strong>{importReport.updated}</strong> {importReport.dry_run ? "would update" : "updated"}
                </span>
                <span data-testid="enjoy-import-stat-skipped">
                  <strong>{importReport.skipped}</strong> skipped
                </span>
                <span data-testid="enjoy-import-stat-errors"
                  style={{ color: importReport.errors ? "var(--enjoy-burgundy, #a23b3b)" : undefined }}>
                  <strong>{importReport.errors}</strong> error{importReport.errors === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 text-[12px]">
              <table className="w-full">
                <thead>
                  <tr className="text-left t-mono uppercase text-[10px] tracking-[0.18em] text-[hsl(var(--ink3,#6b6055))]">
                    <th className="py-1.5 pr-3">Row</th>
                    <th className="py-1.5 pr-3">Action</th>
                    <th className="py-1.5">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(importReport.report || []).slice(0, 100).map((r, i) => (
                    <tr key={i} className="border-t border-[hsl(var(--brass)/0.15)]">
                      <td className="py-1.5 pr-3 t-mono">{r.row}</td>
                      <td className="py-1.5 pr-3" style={{
                        color: r.action === "error" ? "#a23b3b" :
                               r.action === "skipped" ? "#8b6f3a" : "#0f5c1f",
                        fontWeight: 600,
                      }}>{r.action}</td>
                      <td className="py-1.5">
                        {r.title || r.reason || (r.fields && r.fields.join(", ")) || r.id || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(importReport.report || []).length > 100 && (
                <p className="t-mono uppercase text-[10px] tracking-[0.18em] text-[hsl(var(--ink3,#6b6055))] pt-3">
                  +{(importReport.report || []).length - 100} more rows
                </p>
              )}
            </div>
            <div className="p-4 border-t border-[hsl(var(--brass)/0.4)] flex justify-end gap-3">
              <button className="btn-ghost text-[11px] tracking-[0.2em] uppercase"
                onClick={() => setImportReport(null)}
                disabled={importBusy}
                data-testid="enjoy-overlays-import-close">Close</button>
              {importReport.dry_run && (
                <button className="btn-primary"
                  disabled={importBusy || !importFile || (importReport.created + importReport.updated === 0)}
                  onClick={() => importFile && runImport(importFile, false)}
                  data-testid="enjoy-overlays-import-commit">
                  Commit ↑ ({importReport.created + importReport.updated} rows)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
